import { useEffect, useRef, useState } from 'react';
import api, { OPERATING_GROUP_CHANGED_EVENT, OPERATING_GROUP_STORAGE_KEY } from '../../api/api';
import { createIdempotencyKey } from '../../utils/operationKey';

export type ManualProductionPayload = {
  semiFinishedProductId: number;
  quantity: number;
  date: string;
  notes: string | null;
};

type Operation = { key: string; payload: ManualProductionPayload; confirmed: boolean; reconciled: boolean };

export default function useManualProductionOperation(onScopeChanged: () => void, selectedId: number | null) {
  const operations = useRef(new Map<number, Operation>());
  const inFlight = useRef(false);
  const scope = useRef(localStorage.getItem(OPERATING_GROUP_STORAGE_KEY));
  const generation = useRef(0);
  const invalidated = useRef(false);
  const accessBlocked = useRef(scope.current === 'all');
  const scopeChanged = useRef(onScopeChanged);
  scopeChanged.current = onScopeChanged;
  const [saving, setSaving] = useState(false);
  const [errorState, setErrorState] = useState<{ id: number | null; message: string | null } | null>(null);
  const setError = (message: string | null) => setErrorState({ id: selectedId, message });
  const [blocked, setBlocked] = useState(scope.current === 'all');
  const [, update] = useState(0);

  useEffect(() => {
    const invalidate = () => {
      generation.current += 1;
      invalidated.current = true;
      accessBlocked.current = true;
      operations.current.clear();
      setBlocked(true);
      setSaving(false);
      setErrorState(null);
      scopeChanged.current();
    };
    const storageChanged = (event: StorageEvent) => {
      if (event.key === OPERATING_GROUP_STORAGE_KEY && event.newValue !== scope.current) invalidate();
    };
    const operatingGroupChanged = (event: Event) => {
      if ((event as CustomEvent<{ groupId: string | null }>).detail.groupId !== scope.current) invalidate();
    };
    window.addEventListener('storage', storageChanged);
    window.addEventListener(OPERATING_GROUP_CHANGED_EVENT, operatingGroupChanged);
    return () => {
      generation.current += 1;
      window.removeEventListener('storage', storageChanged);
      window.removeEventListener(OPERATING_GROUP_CHANGED_EVENT, operatingGroupChanged);
    };
  }, []);

  const isCurrentScope = () => !invalidated.current
    && localStorage.getItem(OPERATING_GROUP_STORAGE_KEY) === scope.current;

  const submit = async (payload: ManualProductionPayload, refresh: () => Promise<void>) => {
    if (inFlight.current || accessBlocked.current || !isCurrentScope()) return false;
    const request = generation.current;
    const current = () => request === generation.current && isCurrentScope();
    const id = payload.semiFinishedProductId;
    const operation = operations.current.get(id) ?? {
      key: createIdempotencyKey(), payload: { ...payload }, confirmed: false, reconciled: false,
    };
    operations.current.set(id, operation);
    inFlight.current = true;
    setSaving(true);
    setError(null);
    update(value => value + 1);
    try {
      if (!operation.confirmed) {
        try {
          await api.post('/semifinishedproductions', operation.payload, {
            headers: { 'Idempotency-Key': operation.key },
          });
          if (!current()) return false;
          operation.confirmed = true;
        } catch (failure: any) {
          if (!current()) return false;
          const status = failure?.response?.status;
          if (status !== 409) {
            if (status === 403 || status === 404) {
              accessBlocked.current = true;
              setBlocked(true);
            }
            // Admission/validation failures are definitive; uncertain outcomes retain the exact operation.
            if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
              operations.current.delete(id);
            }
            throw failure;
          }
          operation.confirmed = true;
          setErrorState({ id, message: failure?.response?.data?.message
            ?? 'Виробництво вже оброблено або виник конфлікт. Історію оновлено; новий документ автоматично не створюється.' });
        }
      }
      await refresh();
      if (!current()) return false;
      operation.reconciled = true;
      return true;
    } catch (failure: any) {
      if (current()) setErrorState({ id, message: failure?.response?.data?.message ?? (operation.confirmed
        ? 'Не вдалося оновити історію. Повторіть оновлення без повторного створення документа.'
        : 'Не вдалося створити документ виробництва. Повторна спроба збереже початкові дані.') });
      return false;
    } finally {
      inFlight.current = false;
      if (current()) {
        setSaving(false);
        update(value => value + 1);
      }
    }
  };

  // Only an explicit new-document action may retire a confirmed operation.
  const begin = (id: number) => {
    const operation = operations.current.get(id);
    if (!inFlight.current && operation?.reconciled) operations.current.delete(id);
    setError(null);
    update(value => value + 1);
    return operations.current.get(id)?.payload;
  };

  return { submit, begin, saving, error: errorState?.id === selectedId ? errorState?.message : null,
    setError, blocked, isCurrentScope,
    operationFor: (id: number) => operations.current.get(id) };
}
